import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from './apiConfig';
import { Clock, ArrowLeft, CheckCircle, AlertCircle, HelpCircle, Upload, FileText, ArrowRight, Calendar, ChevronDown, ChevronUp, Lock, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { toLocalDateString, getLocalNoonDate } from './utils/timezone';
import { trackEvent } from './lib/analytics';
import { DEFAULT_PHOTOSHOOT_PRICING, getPhotoshootPricing, SHOOT_TYPES } from './photoshootPricing';

const REVISED_PRICING_EFFECTIVE_DATE = '2026-11-01';

const NewPhotoshootBooking = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1); // 1: Calendar, 2: Time Slots, 3: Details & T&C, 4: Success
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    phone: '',
    shoot_type: ''
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);
  const [checkingDates, setCheckingDates] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [bookingWindow, setBookingWindow] = useState({
    mode: 'rolling', // Add mode support
    min_days_ahead: 1,
    max_days_ahead: 60
  });
  const [idFile, setIdFile] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [uploadingId, setUploadingId] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(0); // For forcing re-render once slots are ready
  const [calendarOpen, setCalendarOpen] = useState(true); // For mobile accordion
  const [pricingConfig, setPricingConfig] = useState(DEFAULT_PHOTOSHOOT_PRICING);

  // Enhanced validation states
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // Validation functions
  const validateField = (name, value) => {
    const errors = { ...validationErrors };

    switch (name) {
      case 'user_name':
        if (!value.trim()) {
          errors[name] = 'Name is required';
        } else if (value.trim().length < 2) {
          errors[name] = 'Name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
          errors[name] = 'Name can only contain letters and spaces';
        } else {
          delete errors[name];
        }
        break;

      case 'user_email':
        if (!value.trim()) {
          errors[name] = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          errors[name] = 'Please enter a valid email address';
        } else {
          delete errors[name];
        }
        break;

      case 'phone':
        const cleanPhone = value.replace(/\D/g, ''); // Remove non-digits
        if (!value.trim()) {
          errors[name] = 'Phone number is required';
        } else if (cleanPhone.length !== 10) {
          errors[name] = `Phone number must be exactly 10 digits (you have ${cleanPhone.length})`;
        } else if (!/^[6-9]/.test(cleanPhone)) {
          errors[name] = 'Phone number must start with 6, 7, 8, or 9';
        } else {
          delete errors[name];
        }
        break;

      case 'shoot_type':
        if (!value) errors[name] = 'Shoot type is required';
        else delete errors[name];
        break;

      default:
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateForm = () => {
    const needsPackageChoice = selectedDuration && selectedDuration !== 3;
    const fields = ['user_name', 'user_email', 'phone', ...(selectedDate && toLocalDateString(selectedDate) >= REVISED_PRICING_EFFECTIVE_DATE && needsPackageChoice ? ['shoot_type'] : [])];
    let isValid = true;

    fields.forEach(field => {
      if (!validateField(field, formData[field])) {
        isValid = false;
      }
    });

    if (!acceptedTerms) {
      setValidationErrors(prev => ({ ...prev, terms: 'You must accept the terms and conditions' }));
      isValid = false;
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.terms;
        return newErrors;
      });
    }

    if (!idFile) {
      setValidationErrors(prev => ({ ...prev, idFile: 'Goverment ID proof is required' }));
      isValid = false;
    } else {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.idFile;
        return newErrors;
      });
    }

    return isValid;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, [name]: true }));

    // Real-time validation
    if (touchedFields[name]) {
      validateField(name, value);
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  // Defaults; will be overridden by settings if available
  const defaultDurationOptions = [
    {
      value: '3hr',
      groupLabel: '3 Hour Sessions',
      slots: [
        { id: 'A1', hour: 6, minute: 30, duration: 3, label: '06:30 AM – 09:30 AM', price: 6500, deposit: 2500 },
        { id: 'A2', hour: 11, minute: 0, duration: 3, label: '11:00 AM – 02:00 PM', price: 6500, deposit: 2500 },
        { id: 'A3', hour: 15, minute: 30, duration: 3, label: '03:30 PM – 06:30 PM', price: 6500, deposit: 2500 }
      ],
      price: 6500,
      deposit: 2500,
      description: 'Only for couple and family photoshoots (maximum 10 people)'
    },
    {
      value: '6hr',
      groupLabel: '6 Hour Sessions',
      slots: [
        { id: 'B1', hour: 6, minute: 0, duration: 6, label: '06:00 AM – 12:00 PM', price: 11000, deposit: 3500 },
        { id: 'B2', hour: 13, minute: 0, duration: 6, label: '01:00 PM – 07:00 PM', price: 11000, deposit: 3500 }
      ],
      price: 11000,
      deposit: 3500,
      description: 'Ideal for multiple looks and variety'
    },
    {
      value: '12hr_full',
      groupLabel: '12 Hour Sessions (Full Day)',
      slots: [
        { id: 'C1', hour: 6, minute: 0, duration: 12, label: '06:00 AM – 06:00 PM', price: 20500, deposit: 6000 }
      ],
      price: 20500,
      deposit: 6000,
      description: 'The complete experience for extensive creative needs'
    }
  ];
  const [durationOptions, setDurationOptions] = useState(defaultDurationOptions);

  // Helper to DRY the slot grouping logic
  const processSlotsIntoOptions = (apiSlotsRaw) => {
    const apiSlots = apiSlotsRaw.filter(s => s.active !== false).map(s => ({
      ...s,
      start: s.start_time || s.start, // Handle both conventions
      end: s.end_time || s.end
    }));
    const grouped = {};

    const formatTime = (h, m) => {
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
    };

    apiSlots.forEach(slot => {
      const dur = slot.duration;
      if (!grouped[dur]) {
        grouped[dur] = {
          value: `${dur}hr`,
          groupLabel: `${dur} Hour Sessions${dur === 12 ? ' (Full Day)' : ''}`,
          slots: [],
          price: slot.price,
          deposit: slot.deposit,
          description: dur === 3 ? 'Only for couple and family photoshoots (maximum 10 people)' : dur === 6 ? 'Choose the package that matches your shoot' : 'Choose the package that matches your shoot'
        };
      }

      const [hour, minute] = slot.start.split(':').map(Number);
      const startTime = formatTime(hour, minute);

      // Calculate end time
      const endH = hour + parseFloat(slot.duration);
      const endM = minute;
      const formattedEnd = formatTime(endH, endM);
      const timeRange = `${startTime} - ${formattedEnd}`;

      grouped[dur].slots.push({
        id: slot.id,
        hour,
        minute,
        duration: slot.duration,
        label: slot.label,
        price: slot.price,
        deposit: slot.deposit,
        time: timeRange
      });
    });

    return Object.values(grouped).sort((a, b) => parseInt(a.value) - parseInt(b.value));
  };

  // Load photoshoot and window settings
  useEffect(() => {
    const fetchData = async () => {
      console.log('📡 Fetching photoshoot slots and settings...');
      try {
        const slotsUrl = `${API_BASE_URL}/api/slots`;
        const settingsUrl = `${API_BASE_URL}/api/admin/settings/public?id=global`;

        const [slotsRes, settingsRes] = await Promise.all([
          axios.get(slotsUrl),
          axios.get(settingsUrl)
        ]);

        console.log('✅ Slots API Response:', slotsRes.data);
        console.log('✅ Settings API Response:', settingsRes.data);

        if (slotsRes.data?.success && Array.isArray(slotsRes.data.slots)) {
          console.log('💎 RAW FIRST SLOT PRICE:', slotsRes.data.slots[0]?.price);
          const options = processSlotsIntoOptions(slotsRes.data.slots);
          console.log('🔄 Processed duration options:', JSON.stringify(options, null, 2));
          if (options.length > 0) {
            console.log('🚀 SETTING durationOptions to processed data');
            setDurationOptions(options);
          } else {
            console.warn('⚠️ Processed slots resulted in empty options array.');
          }
        }

        if (settingsRes.data?.success) {
          const settings = settingsRes.data.settings || {};
          if (settings.booking_window) {
            setBookingWindow(settings.booking_window);
          }
          if (settings.photoshoot_pricing) setPricingConfig(settings.photoshoot_pricing);
        }
        setDataLoaded(prev => prev + 1);
      } catch (err) {
        console.error('❌ Error fetching booking data:', err);
        toast.error('Failed to load live prices. Using defaults.');
      }
    };

    fetchData();

    // Initialize session token if not exists
    if (!sessionStorage.getItem('booking_session_token')) {
      const newToken = `st_${Math.random().toString(36).substring(2, 15)}_${Date.now()}`;
      sessionStorage.setItem('booking_session_token', newToken);
    }
  }, []);



  const selectedDuration = selectedSlot ? Number(selectedSlot.duration || String(selectedSlot.value).match(/\d+/)?.[0]) : null;
  const revisedPricingApplies = selectedDate && toLocalDateString(selectedDate) >= REVISED_PRICING_EFFECTIVE_DATE;
  const packageOptions = selectedDuration === 6 ? [
    { value: SHOOT_TYPES.PRIVATE, limit: pricingConfig.private_6?.limit, label: `Private — up to ${pricingConfig.private_6?.limit || 15} people — ₹${Number(pricingConfig.private_6?.price || 12600).toLocaleString('en-IN')}` },
    { value: SHOOT_TYPES.COMMERCIAL, limit: pricingConfig.commercial_6?.limit, label: `Product / Commercial — up to ${pricingConfig.commercial_6?.limit || 20} people — ₹${Number(pricingConfig.commercial_6?.price || 24000).toLocaleString('en-IN')}` },
    { value: SHOOT_TYPES.PRODUCTION, limit: pricingConfig.production_6?.limit, label: `Movie / OTT — up to ${pricingConfig.production_6?.limit || 20} people — ₹${Number(pricingConfig.production_6?.price || 35000).toLocaleString('en-IN')}` },
  ] : selectedDuration === 12 ? [
    { value: SHOOT_TYPES.COMMERCIAL, limit: pricingConfig.commercial_12?.limit, label: `Product / Commercial — up to ${pricingConfig.commercial_12?.limit || 25} people — ₹${Number(pricingConfig.commercial_12?.price || 40000).toLocaleString('en-IN')}` },
    { value: SHOOT_TYPES.PRODUCTION, limit: pricingConfig.production_12?.limit, label: `Movie / OTT — up to ${pricingConfig.production_12?.limit || 35} people — ₹${Number(pricingConfig.production_12?.price || 55000).toLocaleString('en-IN')}` },
  ] : [];
  const selectedPackage = packageOptions.find(option => option.value === formData.shoot_type);
  const effectiveShootType = selectedDuration === 3 ? SHOOT_TYPES.PRIVATE : formData.shoot_type;
  const effectiveHeadcount = selectedDuration === 3 ? pricingConfig.private_3?.limit || 10 : selectedPackage?.limit;
  const pricing = selectedDuration ? (revisedPricingApplies
    ? getPhotoshootPricing({ duration: selectedDuration, shootType: effectiveShootType, headcount: effectiveHeadcount, config: pricingConfig })
    : { valid: true, price: selectedSlot.price, deposit: selectedSlot.deposit || 0, legacy: true }) : null;
  const quotedTax = pricing?.legacy ? pricing.price * 0.20 : 0;
  const quotedDeposit = pricing?.legacy ? pricing.deposit : 0;
  const quotedTotal = pricing
    ? (pricing.legacy ? pricing.price + quotedTax + quotedDeposit : pricing.price)
    : 0;

  // Handle ID file upload
  const handleIdUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or PDF file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setIdFile(file);

    // Clear validation error when file is uploaded
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.idFile;
      return newErrors;
    });

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setIdPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setIdPreview('pdf');
    }
  };

  // Check which dates have bookings when component mounts
  useEffect(() => {
    if (durationOptions.length > 0) {
      checkDateAvailability();
    }
  }, [bookingWindow, durationOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkDateAvailability = async () => {
    setCheckingDates(true);
    try {
      const datesToCheck = [];
      let start, end;

      if (bookingWindow.mode === 'fixed' && bookingWindow.fixed_start_date) {
        start = new Date(bookingWindow.fixed_start_date);
        end = new Date(bookingWindow.fixed_end_date || bookingWindow.fixed_start_date);
      } else {
        start = new Date();
        start.setDate(start.getDate() + (bookingWindow.min_days_ahead || 0));
        end = new Date();
        end.setDate(end.getDate() + (bookingWindow.max_days_ahead || 60));
      }

      // Ensure start is at least today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (start < today) start.setTime(today.getTime());

      // Create a map of days -> total slots count
      // We will mark a day as booked only if ALL slots for that day are unavailable.
      const daySlotsMap = {};
      const allSlotsPayload = [];

      // Identify the "base" slots (3hr slots) to check for each day.
      // Checking 3hr slots is sufficient because if all 3hr slots are blocked, 
      // then 6hr and 12hr are implicitly blocked too (overlap).
      const baseOption = durationOptions.find(o => o.value === '3hr') || durationOptions[0];
      const baseSlots = baseOption?.slots || [];
      const baselineDuration = baseSlots[0]?.duration || 3;

      const current = new Date(start);
      // Iterate through each day in the window
      while (current <= end) {
        const dateStr = toLocalDateString(current);
        daySlotsMap[dateStr] = { total: 0, unavailable: 0 };

        // For this day, generate check entries for each base slot
        baseSlots.forEach(slot => {
          const dateStr = moment(current).format('YYYY-MM-DD');
          const slot_time = moment(`${dateStr} ${slot.hour.toString().padStart(2, '0')}:${slot.minute.toString().padStart(2, '0')}`, 'YYYY-MM-DD HH:mm')
            .utcOffset(330, true)
            .toISOString();

          allSlotsPayload.push({
            slot_time: slot_time,
            duration: baselineDuration,
            day: dateStr // Attach day for grouping later (though backend ignores it)
          });
          daySlotsMap[dateStr].total++;
        });

        current.setDate(current.getDate() + 1);
        if (allSlotsPayload.length > 500) break; // Safety cap (approx 160 days x 3 slots)
      }

      if (allSlotsPayload.length === 0) return;

      const response = await fetch(`${API_BASE_URL}/api/check-availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          duration: baselineDuration,
          date: toLocalDateString(start), // Informational (backend uses slots array)
          slots: allSlotsPayload.map(s => ({ slot_time: s.slot_time, duration: s.duration }))
        })
      });

      const result = await response.json();
      console.log('Availability Check Result:', result);

      if (result.success) {
        // Map results back to days
        result.available_slots.forEach((resSlot, index) => {
          // We match by index since the order is preserved
          const originalReq = allSlotsPayload[index];
          const day = originalReq.day;

          if (!resSlot.available) {
            if (daySlotsMap[day]) {
              daySlotsMap[day].unavailable++;
            }
          }
        });

        const booked = [];
        Object.keys(daySlotsMap).forEach(day => {
          const data = daySlotsMap[day];
          // If ALL slots for the day are unavailable, mark the day as booked
          if (data.total > 0 && data.unavailable === data.total) {
            booked.push(day);
          }
        });

        console.log('Fully Booked Dates:', booked);
        setBookedDates(booked);
      }
    } catch (error) {
      console.error('Error checking dates:', error);
      // In case of error, clearer to NOT block dates than to block everything
      setBookedDates([]);
    } finally {
      setCheckingDates(false);
    }
  };

  const handleDateSelect = async (date, overrideOptions = null) => {
    setSelectedDate(date);
    setLoadingSlots(true);

    try {
      // Check availability for all time slots on this date
      const slotsToCheck = [];
      // Safeguard: Ensure we are using an array. Even if overrideOptions is an event object, we fallback to durationOptions.
      console.log('📍 handleDateSelect CURRENT durationOptions STATE:', JSON.stringify(durationOptions, null, 2));
      let optionsToUse = durationOptions;
      if (Array.isArray(overrideOptions)) {
        console.log('📍 Using overrideOptions');
        optionsToUse = overrideOptions;
      } else if (!Array.isArray(durationOptions)) {
        console.error('durationOptions is not an array! Fallback to default.');
        optionsToUse = defaultDurationOptions;
      }

      optionsToUse.forEach(option => {
        if (option.slots) {
          option.slots.forEach(slot => {
            const yr = date.getFullYear();
            const mo = String(date.getMonth() + 1).padStart(2, '0');
            const dy = String(date.getDate()).padStart(2, '0');
            const dateStrSimple = `${yr}-${mo}-${dy}`;
            const slot_time = moment(`${dateStrSimple} ${slot.hour.toString().padStart(2, '0')}:${slot.minute.toString().padStart(2, '0')}`, 'YYYY-MM-DD HH:mm')
              .utcOffset(330, true)
              .toISOString();

            slotsToCheck.push({
              id: slot.id,
              slot_time: slot_time,
              duration: slot.duration,
              option: option,
              label: slot.label,
              price: slot.price,
              deposit: slot.deposit,
              time: slot.time // Pass the formatted time string
            });
          });
        }
      });

      const response = await fetch(`${API_BASE_URL}/api/check-availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // The top-level duration is used as a fallback or for filtering in some backend logic
          duration: optionsToUse?.[0]?.hours || 3,
          date: toLocalDateString(date),
          slots: slotsToCheck.map(s => ({
            id: s.id,
            slot_time: s.slot_time,
            duration: s.duration
          }))
        })
      });

      const result = await response.json();

      if (result.success) {
        const available = slotsToCheck.map((slot, index) => ({
          ...slot.option,
          // Explicitly include slot-specific details
          id: slot.id,
          slotLabel: slot.label, // The specific name like "Morning Slot"
          price: slot.price,
          deposit: slot.deposit,
          time: slot.time,
          slot_time: slot.slot_time,
          available: result.available_slots[index]?.available || false,
          reason: result.available_slots[index]?.reason
        }));

        setAvailableSlots(available);
        // Removed setCurrentStep(2) to keep user on same page for side-by-side view
      }
    } catch (error) {
      console.error('Error loading slots:', error);
      toast.error('Failed to load available slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setFormData(prev => ({ ...prev, shoot_type: '' }));
    setCurrentStep(2); // Move to Details (Step 2)
    window.scrollTo({ top: 0, behavior: 'smooth' });
    trackEvent('select_item', {
      item_list_name: 'photoshoot_slots',
      item_id: slot.value,
      price: slot.price,
      currency: 'INR',
    });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched for validation
    const allFields = ['user_name', 'user_email', 'phone', ...(revisedPricingApplies && selectedDuration !== 3 ? ['shoot_type'] : [])];
    const newTouchedFields = {};
    allFields.forEach(field => {
      newTouchedFields[field] = true;
    });
    setTouchedFields(newTouchedFields);

    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    if (!pricing?.valid) {
      toast.error(pricing?.reason || 'Complete the shoot details to calculate your price.');
      return;
    }

    setLoading(true);
    try {
      // 1. Upload ID Proof
      let idProofUrl = null;
      let idProofExpiresAt = null;

      if (idFile) {
        setUploadingId(true);
        const formDataId = new FormData();
        formDataId.append('idProof', idFile);

        const idUploadResponse = await fetch(`${API_BASE_URL}/api/upload-id`, {
          method: 'POST',
          body: formDataId
        });

        const idResult = await idUploadResponse.json();

        if (idResult.success) {
          idProofUrl = idResult.url;
          idProofExpiresAt = idResult.expiresAt;
        } else {
          toast.error('Failed to upload ID. Please try again.');
          setLoading(false);
          setUploadingId(false);
          return;
        }
        setUploadingId(false);
      }

      // 2. Prepare Booking Data - Use the specific slot data captured in Step 2
      const bookingData = {
        ...formData,
        shoot_type: revisedPricingApplies ? effectiveShootType : formData.shoot_type,
        guest_count: revisedPricingApplies ? effectiveHeadcount : undefined,
        booking_type: 'photoshoot',
        duration: selectedSlot.value, // e.g. "3hr"
        duration_label: selectedSlot.groupLabel,
        guest_use_time: selectedSlot.slotLabel,
        buffer_after: 'Inclusive',
        date: toLocalDateString(selectedDate),
        amount: pricing.price,
        deposit: quotedDeposit,
        tax_amount: quotedTax,
        total_amount: quotedTotal,
        slot_time: selectedSlot.slot_time,
        slotId: selectedSlot.id,
        id_proof_url: idProofUrl,
        id_proof_expires_at: idProofExpiresAt,
        sessionToken: sessionStorage.getItem('booking_session_token') || `st_fallback_${Date.now()}`
      };

      // 3. Initiate PayU Payment
      trackEvent('begin_checkout', {
        currency: 'INR',
        value: bookingData.total_amount,
        item_id: selectedSlot.value,
      });
      toast.loading('Initiating payment...');
      const response = await fetch(`${API_BASE_URL}/api/payment/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      const result = await response.json();
      console.log('Initiate Payment Result:', result);

      if (!response.ok) {
        setLoading(false);
        const errorMsg = result.message || 'Validation failed';
        const errorDetails = result.errors ? result.errors.map(e => `${e.field}: ${e.message}`).join(', ') : '';
        toast.error(`Error: ${errorMsg}. ${errorDetails}`);
        return;
      }

      if (result.success && result.payu_data) {
        toast.dismiss();

        // Load PayU Bolt Script
        const loadBolt = () => {
          return new Promise((resolve) => {
            if (window.bolt) {
              resolve(true);
              return;
            }
            const script = document.createElement('script');
            script.src = 'https://jssdk.payu.in/bolt/bolt.min.js';
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
          });
        };

        const scriptLoaded = await loadBolt();
        if (!scriptLoaded) {
          toast.error('Failed to load payment gateway');
          setLoading(false);
          return;
        }

        // Launch Bolt
        trackEvent('add_payment_info', {
          currency: 'INR',
          value: bookingData.total_amount,
          transaction_id: result.payu_data.txnid,
        });
        window.bolt.launch(result.payu_data, {
          responseHandler: async function (BOLT) {
            if (BOLT.response.txnStatus === 'SUCCESS') {
              toast.loading('Verifying payment...');
              try {
                const verifyResponse = await fetch(`${API_BASE_URL}/api/payment/verify-bolt`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    txnid: result.payu_data.txnid,
                    status: BOLT.response.txnStatus,
                    payuResponse: BOLT.response
                  })
                });

                const verifyResult = await verifyResponse.json();

                if (verifyResult.success) {
                  toast.dismiss();
                  toast.success('Payment Successful!');
                  navigate(`/payment/success?txnid=${result.payu_data.txnid}&booking_id=${verifyResult.booking_id}`);
                } else {
                  toast.dismiss();
                  toast.error(verifyResult.message || 'Payment verification failed');
                  navigate(`/payment/failure?txnid=${result.payu_data.txnid}&reason=verification_failed`);
                  setLoading(false);
                }
              } catch (e) {
                console.error('Verification call failed', e);
                toast.error('Verification error. Please contact support.');
                setLoading(false);
              }
            } else {
              trackEvent('payment_failed', {
                transaction_id: result.payu_data.txnid,
                reason: BOLT.response.txnMessage || 'transaction_failed',
              });
              toast.error('Payment Failed: ' + (BOLT.response.txnMessage || 'Transaction failed'));
              setLoading(false);
            }
          },
          catchException: function (BOLT) {
            console.error('Bolt Exception:', BOLT);
            trackEvent('payment_failed', {
              transaction_id: result.payu_data.txnid,
              reason: 'cancelled_or_exception',
            });
            toast.error('Payment cancelled or error occurred');
            setLoading(false);
          }
        });
      } else {
        toast.dismiss();
        toast.error(result.message || 'Payment initiation failed');
        setLoading(false);
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to create booking');
      setLoading(false);
    }
  };

  const isDateDisabled = (date) => {
    // Extract date-only string in local timezone to match bookedDates format
    const dateStr = toLocalDateString(date);
    return bookedDates.includes(dateStr);
  };

  return (
    <div className="min-h-screen bg-[#FBF8F3] scroll-smooth" style={{ fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* ─── Top Navigation Bar ─── */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-gray-100/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <button
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                if (currentStep === 1) navigate('/');
                else {
                  setCurrentStep(currentStep - 1);
                  if (currentStep === 2) setCalendarOpen(true);
                }
              }}
              className="flex items-center gap-2 text-sm font-medium text-[#471F21] hover:text-[#5a3a3c] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </button>

            <h1 className="text-lg sm:text-xl font-bold tracking-tight text-[#471F21]">
              Photoshoot Booking
            </h1>

            {/* Step Progress — minimal pills */}
            <div className="flex items-center gap-1.5">
              {[
                { step: 1, label: 'Select' },
                { step: 2, label: 'Details' },
                { step: 3, label: 'Confirm' }
              ].map((item) => (
                <div key={item.step} className="flex items-center gap-1.5">
                  <div
                    className={`flex items-center justify-center rounded-full transition-all duration-300 text-xs font-semibold ${currentStep >= item.step
                      ? 'w-7 h-7 bg-[#471F21] text-white shadow-sm'
                      : 'w-7 h-7 bg-gray-100 text-gray-400'
                      } ${currentStep === item.step ? 'ring-2 ring-[#471F21]/20 ring-offset-1' : ''}`}
                  >
                    {currentStep > item.step ? (
                      <CheckCircle className="w-3.5 h-3.5" />
                    ) : (
                      item.step
                    )}
                  </div>
                  {item.step < 3 && (
                    <div className={`w-6 sm:w-8 h-px transition-colors duration-300 ${currentStep > item.step ? 'bg-[#471F21]' : 'bg-gray-200'
                      }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Maintenance Banner ─── */}
      {new Date() < new Date('2026-07-26') && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-center gap-2.5 text-center">
            <span className="text-amber-600 text-lg leading-none">🔧</span>
            <p className="text-sm font-medium text-amber-800">
              Maintenance Break — <span className="font-semibold">July 1st – July 25th</span>. Bookings for these dates are unavailable.
            </p>
          </div>
        </div>
      )}

      {/* ─── Main Content ─── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">

        {/* ═══ STEP 1: Date & Time Selection ═══ */}
        {currentStep === 1 && (
          <div className="animate-fadeIn">
            {/* Desktop: 2-column grid / Mobile: stacked */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">

              {/* ── LEFT COLUMN: Calendar ── */}
              <div className="lg:col-span-4">
                <div className="lg:sticky lg:top-24 space-y-4">
                  <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: 'var(--shadow-card)' }}>
                    {/* Mobile: Accordion toggle */}
                    <button
                      className="lg:hidden w-full flex items-center justify-between px-5 py-4"
                      onClick={() => setCalendarOpen(!calendarOpen)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-[#471F21]/5 flex items-center justify-center">
                          <Calendar className="w-4 h-4 text-[#471F21]" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-semibold text-gray-900">
                            {selectedDate
                              ? selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                              : 'Select a Date'}
                          </p>
                          <p className="text-[11px] text-gray-400">Tap to {calendarOpen ? 'collapse' : 'expand'}</p>
                        </div>
                      </div>
                      {calendarOpen ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </button>

                    {/* Desktop: Always visible header */}
                    <div className="hidden lg:block px-6 pt-6 pb-2">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-9 h-9 rounded-xl bg-[#471F21]/5 flex items-center justify-center">
                          <Calendar className="w-4 h-4 text-[#471F21]" />
                        </div>
                        <h2 className="text-base font-bold text-gray-900 tracking-tight">Select Date</h2>
                      </div>
                    </div>

                    {/* Calendar Content */}
                    <div className={`transition-all duration-300 ease-in-out overflow-hidden ${calendarOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 lg:max-h-[600px] opacity-0 lg:opacity-100'
                      }`}>
                      <div className="px-4 pb-5 pt-2">
                        {checkingDates ? (
                          <div className="flex flex-col items-center justify-center py-16">
                            <div className="w-10 h-10 rounded-full border-2 border-[#471F21]/20 border-t-[#471F21] animate-spin mb-4"></div>
                            <p className="text-sm text-gray-400 font-medium">Loading calendar...</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <DatePicker
                              selected={selectedDate}
                              onChange={(date) => {
                                handleDateSelect(date);
                                // Auto-close on mobile after selection
                                if (window.innerWidth < 1024) {
                                  setTimeout(() => setCalendarOpen(false), 200);
                                }
                              }}
                              minDate={(() => {
                                if (bookingWindow.mode === 'fixed' && bookingWindow.fixed_start_date) {
                                  return new Date(bookingWindow.fixed_start_date);
                                }
                                return new Date(Date.now() + bookingWindow.min_days_ahead * 24 * 60 * 60 * 1000);
                              })()}
                              maxDate={(() => {
                                if (bookingWindow.mode === 'fixed' && bookingWindow.fixed_end_date) {
                                  return new Date(bookingWindow.fixed_end_date);
                                }
                                return new Date(Date.now() + bookingWindow.max_days_ahead * 24 * 60 * 60 * 1000);
                              })()}
                              filterDate={(date) => !isDateDisabled(date)}
                              inline
                              disabledKeyboardNavigation
                              showDisabledMonthNavigation
                              calendarClassName="custom-calendar"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Info footnote */}
                  <div className="hidden lg:block px-1">
                    <p className="text-[11px] text-gray-400 leading-relaxed">
                      Dates are available for the next {bookingWindow.max_days_ahead || 30} days.
                      {bookedDates.length > 0 && (
                        <span className="text-gray-300"> · {bookedDates.length} dates fully booked</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* ── RIGHT COLUMN: Slots ── */}
              <div className="lg:col-span-8">
                <div className="bg-white rounded-2xl min-h-[480px]" style={{ boxShadow: 'var(--shadow-card)' }}>
                  {/* Slots Header */}
                  <div className="px-5 sm:px-8 pt-6 sm:pt-8 pb-5 sm:pb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-[#471F21]/5 flex items-center justify-center">
                          <Clock className="w-4 h-4 text-[#471F21]" />
                        </div>
                        <h2 className="text-base sm:text-lg font-bold text-gray-900 tracking-tight">Available Sessions</h2>
                      </div>
                      {selectedDate && (
                        <div className="text-right">
                          <p className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">Date</p>
                          <p className="text-sm font-bold text-[#471F21]">
                            {selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="h-px bg-gray-100 mx-5 sm:mx-8" />

                  {/* Slots Content */}
                  <div className="px-5 sm:px-8 py-6 sm:py-8">
                    {loadingSlots ? (
                      /* ── Loading State ── */
                      <div className="flex flex-col items-center justify-center py-16">
                        <div className="relative mb-6">
                          <div className="w-14 h-14 rounded-full border-[3px] border-gray-100 border-t-[#471F21] animate-spin"></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-[#471F21] animate-pulse-soft" />
                          </div>
                        </div>
                        <p className="text-sm font-semibold text-gray-700">Finding available sessions</p>
                        <p className="text-xs text-gray-400 mt-1">Checking live availability...</p>
                      </div>
                    ) : !selectedDate ? (
                      /* ── No Date Selected State ── */
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mb-5">
                          <Calendar className="w-7 h-7 text-gray-300" />
                        </div>
                        <p className="text-base font-semibold text-gray-800 mb-1">Select a date</p>
                        <p className="text-sm text-gray-400 max-w-xs">
                          Choose an available date on the calendar to view available photoshoot sessions.
                        </p>
                      </div>
                    ) : availableSlots.length === 0 ? (
                      /* ── Empty State ── */
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mb-5">
                          <Calendar className="w-7 h-7 text-gray-300" />
                        </div>
                        <p className="text-base font-semibold text-gray-800 mb-1">No sessions available</p>
                        <p className="text-sm text-gray-400 max-w-xs">
                          This date is fully booked. Try selecting a different date from the calendar.
                        </p>
                      </div>
                    ) : (
                      /* ── Sessions List ── */
                      <div className="space-y-10 stagger-children">
                        {Object.values(availableSlots.reduce((acc, slot) => {
                          const key = slot.value;
                          if (!acc[key]) {
                            acc[key] = {
                              title: slot.groupLabel,
                              description: slot.description,
                              slots: []
                            };
                          }
                          acc[key].slots.push(slot);
                          return acc;
                        }, {})).map((group) => (
                          <div key={group.title}>
                            {/* Session Category Header */}
                            <div className="mb-5">
                              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                                {group.title}
                              </h3>
                              {group.description && (
                                <p className="text-xs text-gray-400 mt-0.5">{group.description}</p>
                              )}
                            </div>

                            {/* Slot Cards Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                              {group.slots.map((slot) => (
                                <button
                                  key={slot.time + slot.id}
                                  onClick={() => slot.available && handleSlotSelect(slot)}
                                  disabled={!slot.available}
                                  className={`relative text-left w-full rounded-xl p-4 sm:p-5 transition-all duration-200 group min-h-[100px] ${slot.available
                                    ? 'bg-white border border-gray-150 hover:border-[#471F21]/30 cursor-pointer active:scale-[0.98]'
                                    : 'bg-gray-50/60 border border-gray-100 cursor-not-allowed'
                                    }`}
                                  style={slot.available ? { boxShadow: 'var(--shadow-sm)' } : {}}
                                  onMouseEnter={(e) => { if (slot.available) e.currentTarget.style.boxShadow = 'var(--shadow-card-hover)'; }}
                                  onMouseLeave={(e) => { if (slot.available) e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
                                >
                                  {/* Top row: Label + Badge */}
                                  <div className="flex items-center justify-between mb-3">
                                    <span className={`text-[10px] font-bold uppercase tracking-widest ${slot.available ? 'text-[#471F21]/60' : 'text-gray-400'
                                      }`}>
                                      {slot.slotLabel || 'Session'}
                                    </span>
                                    {!slot.available && (
                                      <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                                        <Lock className="w-2.5 h-2.5" />
                                        Unavailable
                                      </span>
                                    )}
                                  </div>

                                  {/* Time */}
                                  <p className={`text-lg sm:text-xl font-extrabold tracking-tight mb-3 ${slot.available ? 'text-gray-900' : 'text-gray-300'
                                    }`}>
                                    {slot.time}
                                  </p>

                                  {/* Price row */}
                                  <div className="flex items-end justify-between">
                                    <div>
                                      <span className={`text-xl sm:text-2xl font-extrabold tracking-tight ${slot.available ? 'text-[#471F21]' : 'text-gray-300'
                                        }`}>
                                        From ₹{({ 3: 7200, 6: 12600, 12: 40000 }[Number(slot.duration || String(slot.value).match(/\d+/)?.[0])] || 0).toLocaleString('en-IN')}
                                      </span>
                                      {slot.available && (
                                        <p className="text-[10px] text-gray-400 mt-0.5">all-inclusive price varies by shoot type & crew</p>
                                      )}
                                    </div>
                                    {slot.available && (
                                      <div className="w-8 h-8 rounded-full bg-[#471F21] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-200">
                                        <ArrowRight className="w-3.5 h-3.5" />
                                      </div>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Mobile info note */}
                <div className="lg:hidden mt-3 px-1">
                  <p className="text-[11px] text-gray-400 text-center">
                    Dates available for the next {bookingWindow.max_days_ahead || 30} days
                    {bookedDates.length > 0 && ` · ${bookedDates.length} dates booked`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ STEP 2: Details & Terms ═══ */}
        {currentStep === 2 && (
          <div className="max-w-2xl mx-auto animate-fadeIn">
            <div className="bg-white rounded-2xl p-6 sm:p-8" style={{ boxShadow: 'var(--shadow-card)' }}>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-6 tracking-tight">
                Your Details
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Selected Slot Summary — compact card */}
                <div className="rounded-xl p-4 bg-[#FBF5EB] border border-[#471F21]/10">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#471F21]/50">Selected Session</span>
                    <button
                      type="button"
                      onClick={() => { setCurrentStep(1); setCalendarOpen(true); }}
                      className="text-[11px] font-semibold text-[#471F21] hover:underline"
                    >
                      Change
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm mb-3">
                    <div>
                      <span className="text-[10px] text-gray-400 font-medium uppercase block">Date</span>
                      <span className="font-semibold text-gray-900 text-sm">{selectedDate?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-gray-400 font-medium uppercase block">Time</span>
                      <span className="font-semibold text-gray-900 text-sm">{selectedSlot?.time}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-[10px] text-gray-400 font-medium uppercase block">Session</span>
                      <span className="font-semibold text-gray-900 text-sm">{selectedSlot?.groupLabel}</span>
                    </div>
                  </div>

                  <div className="border-t border-[#471F21]/10 pt-3 space-y-1">
                    <div className="flex justify-between items-end pt-2 mt-1">
                      <div className="flex flex-col">
                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Total Payable</span>
                        <span className="text-[10px] text-gray-400 italic mt-0.5">
                          {pricing?.valid
                            ? (pricing.legacy ? 'Inclusive of taxes and refundable deposit' : 'Final all-inclusive price')
                            : 'Choose a package below for your tariff'}
                        </span>
                      </div>
                      <span className="font-extrabold text-[#471F21] text-lg leading-none">
                        {pricing?.valid ? `₹${quotedTotal.toLocaleString('en-IN')}` : '—'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact Details */}
                <div className="space-y-4">
                  {/* Full Name */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Full Name <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="user_name"
                        value={formData.user_name}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        required
                        className={`w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:border-transparent transition-all ${touchedFields.user_name && validationErrors.user_name
                          ? 'border-red-400 focus:ring-red-100'
                          : touchedFields.user_name && !validationErrors.user_name
                            ? 'border-green-400 focus:ring-green-100'
                            : 'border-gray-200 focus:ring-[#471F21]/10'
                          }`}
                        placeholder="Enter your full name"
                      />
                      {touchedFields.user_name && !validationErrors.user_name && formData.user_name && (
                        <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-green-500" />
                      )}
                      {touchedFields.user_name && validationErrors.user_name && (
                        <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                      )}
                    </div>
                    {touchedFields.user_name && validationErrors.user_name && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {validationErrors.user_name}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Email <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        name="user_email"
                        value={formData.user_email}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        required
                        className={`w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:border-transparent transition-all ${touchedFields.user_email && validationErrors.user_email
                          ? 'border-red-400 focus:ring-red-100'
                          : touchedFields.user_email && !validationErrors.user_email
                            ? 'border-green-400 focus:ring-green-100'
                            : 'border-gray-200 focus:ring-[#471F21]/10'
                          }`}
                        placeholder="your@email.com"
                      />
                      {touchedFields.user_email && !validationErrors.user_email && formData.user_email && (
                        <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-green-500" />
                      )}
                      {touchedFields.user_email && validationErrors.user_email && (
                        <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                      )}
                    </div>
                    {touchedFields.user_email && validationErrors.user_email && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {validationErrors.user_email}
                      </p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Phone Number <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        required
                        pattern="[0-9]{10}"
                        className={`w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:border-transparent transition-all ${touchedFields.phone && validationErrors.phone
                          ? 'border-red-400 focus:ring-red-100'
                          : touchedFields.phone && !validationErrors.phone
                            ? 'border-green-400 focus:ring-green-100'
                            : 'border-gray-200 focus:ring-[#471F21]/10'
                          }`}
                        placeholder="9876543210"
                      />
                      {touchedFields.phone && !validationErrors.phone && formData.phone && (
                        <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-green-500" />
                      )}
                      {touchedFields.phone && validationErrors.phone && (
                        <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-500" />
                      )}
                    </div>
                    {touchedFields.phone && validationErrors.phone && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {validationErrors.phone}
                      </p>
                    )}
                  </div>

                  {revisedPricingApplies && <>
                  {selectedDuration === 3 ? (
                    <div className="rounded-xl p-4 border bg-[#FBF5EB] border-[#471F21]/10">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-[#471F21]/60">3-hour private session</p>
                      <p className="mt-1 text-sm font-semibold text-[#471F21]">For couple and family photoshoots only · Up to {pricingConfig.private_3?.limit || 10} people total · ₹{Number(pricingConfig.private_3?.price || 7200).toLocaleString('en-IN')} all-inclusive</p>
                      <p className="mt-1 text-xs text-gray-500">Headcount includes clients, photographer / videographer, makeup, assistants and crew. Commercial, product, brand, movie and OTT shoots must select a 6- or 12-hour session. No taxes, fees, or deposit are added at checkout.</p>
                    </div>
                  ) : <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Package &amp; shoot type <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <select
                        name="shoot_type"
                        value={formData.shoot_type}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        className={`w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:border-transparent transition-all appearance-none bg-white ${touchedFields.shoot_type && validationErrors.shoot_type
                          ? 'border-red-400 focus:ring-red-100'
                          : 'border-gray-200 focus:ring-[#471F21]/10'
                          }`}
                      >
                        <option value="">Choose a package</option>
                        {packageOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                    <p className="mt-1.5 text-[11px] text-gray-400">The stated headcount includes everyone entering the property. Declared groups up to 20 people have a 1–2 person arrival buffer.</p>
                    {touchedFields.shoot_type && validationErrors.shoot_type && (
                      <p className="mt-1 text-xs text-red-500">{validationErrors.shoot_type}</p>
                    )}
                  </div>

                  {pricing && formData.shoot_type && (
                    <div className={`rounded-xl p-4 border ${pricing.valid ? 'bg-[#FBF5EB] border-[#471F21]/10' : 'bg-amber-50 border-amber-200'}`}>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-[#471F21]/60">Your tariff</p>
                      {pricing.valid ? (
                        <>
                          <p className="mt-1 text-xl font-extrabold text-[#471F21]">₹{pricing.price.toLocaleString('en-IN')} <span className="text-xs font-medium text-gray-500">{pricing.legacy ? 'base price' : 'all-inclusive price'}</span></p>
                          <p className="mt-1 text-xs text-gray-500">{pricing.legacy ? `₹${quotedTotal.toLocaleString('en-IN')} payable including taxes and refundable deposit.` : 'This is the final payable amount. All taxes and fees are included; no deposit is added at checkout.'}</p>
                        </>
                      ) : <p className="mt-1 text-sm font-medium text-amber-900">{pricing.reason}</p>}
                    </div>
                  )}
                  </>}
                  </>}

                  {/* ID Verification Section */}
                  <div className="pt-6 mt-2">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-9 h-9 rounded-xl bg-[#471F21]/5 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-[#471F21]" />
                      </div>
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                        ID Verification
                      </h3>
                    </div>

                    <div className="bg-amber-50/80 border border-amber-200/60 rounded-xl p-4 mb-5">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-bold text-amber-900 mb-0.5">
                            Government ID Proof Required
                          </p>
                          <p className="text-[11px] text-amber-700 leading-relaxed">
                            Upload Aadhaar or any Govt-issued ID. Bookings without valid ID will be rejected without refund.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className={`relative border-2 border-dashed rounded-xl p-6 sm:p-8 transition-all text-center ${idFile ? 'bg-green-50/30 border-green-200' : 'bg-gray-50/50 border-gray-200 hover:border-[#471F21]/20'}`}>
                      <input
                        type="file"
                        onChange={handleIdUpload}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        accept="image/*,.pdf"
                      />

                      {!idFile ? (
                        <div className="flex flex-col items-center">
                          <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center mb-3" style={{ boxShadow: 'var(--shadow-sm)' }}>
                            <Upload className="w-5 h-5 text-[#471F21]" />
                          </div>
                          <p className="text-sm font-semibold text-gray-800 mb-0.5">Upload ID Proof</p>
                          <p className="text-xs text-gray-400">JPG, PNG, or PDF (max 5MB)</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <div className="relative mb-3">
                            {idPreview === 'pdf' ? (
                              <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center" style={{ boxShadow: 'var(--shadow-md)' }}>
                                <FileText className="w-8 h-8 text-[#471F21]" />
                              </div>
                            ) : (
                              <img src={idPreview} alt="ID Preview" className="w-20 h-20 object-cover rounded-xl" style={{ boxShadow: 'var(--shadow-md)' }} />
                            )}
                            <div className="absolute -top-1.5 -right-1.5 bg-green-500 text-white p-0.5 rounded-full">
                              <CheckCircle className="w-3.5 h-3.5" />
                            </div>
                          </div>
                          <p className="text-sm font-semibold text-green-700">{idFile.name}</p>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); setIdFile(null); setIdPreview(null); }}
                            className="mt-1 text-xs text-red-500 font-medium hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                    {validationErrors.idFile && (
                      <p className="text-xs text-red-500 flex items-center gap-1 mt-2">
                        <AlertCircle className="w-3 h-3" />
                        {validationErrors.idFile}
                      </p>
                    )}
                  </div>
                </div>

                {/* Terms & Conditions */}
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 bg-gray-50/50">
                    <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">Terms & Conditions</h3>
                  </div>
                  <div className="px-5 py-4 text-sm text-gray-600 max-h-44 overflow-y-auto space-y-5 leading-relaxed">
                    <div>
                      <h4 className="font-bold text-[#471F21] mb-1.5 text-xs uppercase tracking-wide">Cancellation, Refund & Rescheduling</h4>
                      <ul className="list-disc pl-4 space-y-1 text-xs text-gray-600">
                        <li>Photoshoot cancellations made <strong>7 days or more</strong> before the booking are eligible for a <strong>50% refund</strong>. No refunds will be issued for cancellations made within <strong>7 days</strong> of the scheduled booking.</li>
                        <li>Rescheduling is not permitted within <strong>7 days</strong> of the booking. In genuine emergencies, one reschedule may be approved at the sole discretion of <strong>Thaathayya Illu</strong>, subject to availability, provided the request is made via WhatsApp or email and the rescheduled booking is availed within <strong>one month</strong> of the original booking date.</li>
                        <li>Eligible refunds will be processed via <strong>PayU</strong> within <strong>7-10 working days</strong> upon receipt of your Booking ID or booking confirmation via WhatsApp or email.</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-bold text-[#471F21] mb-1.5 text-xs uppercase tracking-wide">Please Know This Before You Proceed</h4>
                      <ul className="list-disc pl-4 space-y-1 text-xs text-gray-600">
                        <li>Thaathayya Illu is a private family home, not a commercial studio.</li>
                        <li><strong>Your booked time slot is fixed.</strong> Time is counted gate-to-gate.</li>
                        <li>Please arrive 10–15 minutes early. No extra time for delays.</li>
                        <li>Overtime is charged at <strong>₹2,000/hr for private</strong>, <strong>₹3,000/hr for commercial / brand</strong>, and <strong>₹4,000/hr for movie / OTT / production</strong> shoots, subject to availability.</li>
                        <li><strong>Headcount and shoot type must be declared accurately.</strong> Everyone entering the property—including clients, models, photographers, videographers, makeup artists, stylists, assistants and production crew—counts towards the total.</li>
                        <li>Product, catalogue, e-commerce, campaign, advertising and other business-use shoots must be booked as <strong>Commercial / Brand / Product</strong>.</li>
                        <li>Furniture must not be dragged. Request caretaker assistance.</li>
                        <li>The large portrait in the central area must not be moved.</li>
                        <li>Climbing on the roof is strictly prohibited (fragile tiles).</li>
                        <li>Damage to property attracts a minimum fine of <strong>₹2,500</strong>.</li>
                      </ul>
                    </div>
                  </div>
                  <div className="px-5 py-4 border-t border-gray-100 bg-white">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={(e) => {
                          setAcceptedTerms(e.target.checked);
                          if (e.target.checked) {
                            setValidationErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.terms;
                              return newErrors;
                            });
                          }
                        }}
                        className="w-4 h-4 mr-2.5 rounded"
                        style={{ accentColor: '#471F21' }}
                      />
                      <span className="text-xs font-medium text-gray-900">
                        I accept the terms and conditions <span className="text-red-400">*</span>
                      </span>
                    </label>
                    {validationErrors.terms && (
                      <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {validationErrors.terms}
                      </p>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || Object.keys(validationErrors).length > 0 || !pricing?.valid}
                  className={`w-full py-3.5 px-6 rounded-xl font-semibold text-sm transition-all duration-200 disabled:cursor-not-allowed ${(Object.keys(validationErrors).length > 0 || !pricing?.valid)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'text-white hover:opacity-90 active:scale-[0.99]'
                    }`}
                  style={Object.keys(validationErrors).length === 0 ? { backgroundColor: '#471F21', boxShadow: '0 4px 14px rgba(71, 31, 33, 0.25)' } : {}}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>
                      Processing...
                    </div>
                  ) : !pricing?.valid ? (
                    'Complete eligible shoot details'
                  ) : Object.keys(validationErrors).length > 0 ? (
                    'Please fix errors above'
                  ) : (
                    'Proceed to Payment'
                  )}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* ═══ STEP 3: Success ═══ */}
        {currentStep === 3 && (
          <div className="max-w-md mx-auto animate-fadeIn">
            <div className="bg-white rounded-2xl p-8 sm:p-10 text-center" style={{ boxShadow: 'var(--shadow-lg)' }}>
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2 tracking-tight">
                Booking Confirmed!
              </h2>
              <p className="text-sm text-gray-500 mb-8 leading-relaxed max-w-xs mx-auto">
                Your photoshoot has been booked. You'll receive a confirmation via email and WhatsApp shortly.
              </p>
              <button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  navigate('/');
                }}
                className="inline-flex items-center gap-2 text-white py-3 px-8 rounded-xl text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.98]"
                style={{ backgroundColor: '#471F21', boxShadow: '0 4px 14px rgba(71, 31, 33, 0.25)' }}
              >
                Back to Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewPhotoshootBooking;
